/* assets/labels-and-badges.js
 * MSRP-first sale badge logic (Reclop)
 * - If productData.msrp_price exists and is greater than productData.price,
 *   use it as the compare base for {sale} and sale-label visibility.
 * - Otherwise, fallback to Shopify compare_at_price behavior.
 *
 * PDP position logic
 * - Card uses label.settings.position
 * - PDP uses label.settings.pdp_position first
 * - If pdp_position is "follow-card", fallback to card position
 */

requestAnimationFrame(() => {
  document.addEventListener('alpine:init', () => {
    Alpine.store('xBadges', {
      fixedPositionTemplate: `<div
        class="x-badge-{label-id} x-badge-container pointer-events-none{container-img-class} ltr"
        {preview-show-condition}
      >
        {content}
      </div>`,

      customPositionTemplate: `<div
        class="x-badge-{label-id} x-badge-container min-w-fit max-w-full max-h-full pointer-events-none{container-css-class} ltr"
        x-data="{
          contentHeight: 1,
          rePosition() {
            this.$nextTick(() => {
              this.contentHeight = this.$refs.content ? this.$refs.content.offsetHeight : contentHeight;
            });
          }
        }"
        x-intersect.once="rePosition();
          if (Shopify.designMode) {
            window.addEventListener('resize', () => {
              if ($store.xBadges.lastWindowWidth != window.innerWidth) {
                rePosition();
              }
            });
          } else {
            installMediaQueryWatcher('(min-width: 768px)', () => rePosition());
          }"
        {preview-show-condition}
        :style="'{css-position} min-height: ' + contentHeight + 'px'"
      >
        {content}
      </div>`,

      productDetailTemplate: `
      {teleport-template-open}
        <div
          class="x-badge-{label-id} x-badge-container min-w-fit max-w-full max-h-full bottom-0 pointer-events-none{container-css-class}{container-img-class}"
          style="{css-position}"
          x-data="{
            contentHeight: 1,
            rePosition() {
              this.$nextTick(() => {
                this.contentHeight = this.$refs.content ? this.$refs.content.offsetHeight : contentHeight;
              });
            }
          }"
          x-intersect.once="rePosition();
            if (Shopify.designMode) {
              window.addEventListener('resize', () => {
                if ($store.xBadges.lastWindowWidth != window.innerWidth) {
                  rePosition();
                }
              });
            } else {
              installMediaQueryWatcher('(min-width: 768px)', () => rePosition());
            }"
        >
          {content}
        </div>
      {teleport-template-close}
      `,

      outSideImageTemplate: `<div class="hidden"
        x-data="{
          setPosition() {
            const element = $el.closest('.card-product').getElementsByClassName('{queryEL}');
            for (let i = 0; i < element.length; i++) {
              element[i].innerHTML += $el.innerHTML;
            }
          }
        }"
        x-init="setPosition()">
        <div
          class="x-badge-{label-id} x-badge-container min-w-fit max-w-full max-h-full bottom-0 pointer-events-none{container-img-class}"
          {preview-show-condition}
        >
          {content}
        </div>
      </div>`,

      previewActiveBlock: window.xBadgesPreviewActiveBlock,

      init() {
        if (Shopify.designMode) {
          document.addEventListener('shopify:block:select', (event) => {
            if (!event.target.classList.contains('x-badges-block-preview')) return;

            const blockId = event.target.getAttribute('block-id');
            this.previewActiveBlock = blockId;
            window.xBadgesPreviewActiveBlock = blockId;
            document.dispatchEvent(new CustomEvent('eurus:badges:block-select'));
          });
        }
      },

      load(el, callback = () => {}, container = null) {
        if (container) el.container = container;

        const sliderEl = el.closest('[x-data-slider]');
        if (sliderEl) {
          if (!sliderEl.classList.contains('is-initialized')) {
            const sectionId = el.closest('[x-data-slider]').getAttribute('x-data-slider');
            document.addEventListener(`eurus:${sectionId}:splide-ready`, () => {
              this.doLoad(el, callback);
            });
          } else {
            this.doLoad(el, callback);
          }
        } else {
          this.doLoad(el, callback);
        }
      },

      loadEagerSaleLabel(el) {
        if (Shopify.designMode) return;
        if (!el || !el.hasAttribute('data-x-badges-eager-sale')) return;
        if (!el.hasAttribute('x-labels-data')) return;
        if (el.hasAttribute('data-x-badges-sale-eager-loaded')) return;

        const productData = xParseJSON(el.getAttribute('x-labels-data'));
        if (!productData || productData.container !== 'card') return;

        const allLabels = document.getElementsByClassName('x-badges-block-data');
        let hasRenderedSaleLabel = false;

        for (let i = 0; i < allLabels.length; i++) {
          const label = xParseJSON(allLabels[i].getAttribute('x-badges-block-data'));
          if (!label || label.type !== 'sale-label') continue;
          if (!label.enable && !productData.isXBadgesPreview) continue;
          if (!this.canShow(label, productData)) continue;
        
          label.settings.icon = allLabels[i].getAttribute('x-badges-icon');
          this.appendLabel(el, label, productData);
          hasRenderedSaleLabel = true;
        }
      
        if (hasRenderedSaleLabel) {
          el.setAttribute('data-x-badges-sale-eager-loaded', 'true');
        }
      },

      doLoad(el, callback = () => {}) {
        this.initAllLabels(el);

        if (Shopify.designMode) {
          const productData = xParseJSON(el.getAttribute('x-labels-data'));
          document.addEventListener('shopify:section:load', () => {
            if (productData && !productData.isXBadgesPreview) {
              this.initAllLabels(el);
            }
          });
        }

        callback(el);
      },

      initAllLabels(el) {
        const productData = xParseJSON(el.getAttribute('x-labels-data'));
        if (!productData) return;

        if (Shopify.designMode) {
          const currentLabels = el.getElementsByClassName('x-badge-container');
          if (currentLabels.length > 0) {
            const labelsNum = currentLabels.length;
            for (let i = 0; i < labelsNum; i++) {
              currentLabels[0].remove();
            }
          }
        }

        const allLabels = document.getElementsByClassName('x-badges-block-data');
        for (let i = 0; i < allLabels.length; i++) {
          const label = xParseJSON(allLabels[i].getAttribute('x-badges-block-data'));
          if (!label.enable && !productData.isXBadgesPreview) continue;

          label.settings.icon = allLabels[i].getAttribute('x-badges-icon');
          this.appendLabel(el, label, productData);
        }

        el.removeAttribute('x-labels-data');
      },

      // PDP 실제 위치 계산
      getResolvedPosition(label, productData) {
        if (productData.container !== 'product-info') {
          return label.settings.position;
        }

        const pdpPosition = label.settings.pdp_position || 'follow-card';
        return pdpPosition === 'follow-card' ? label.settings.position : pdpPosition;
      },

      // PDP custom 좌표 계산
      getResolvedCustomPosition(label, productData) {
        if (productData.container === 'product-info') {
          return {
            horizontal: Number(label.settings.pdp_horizontal_position ?? label.settings.horizontal_position ?? 0),
            vertical: Number(label.settings.pdp_vertical_position ?? label.settings.vertical_position ?? 0)
          };
        }

        return {
          horizontal: Number(label.settings.horizontal_position ?? 0),
          vertical: Number(label.settings.vertical_position ?? 0)
        };
      },

      appendLabel(el, label, productData) {
        if (
          productData.container === 'card' &&
          label.type === 'sale-label' &&
          el.hasAttribute('data-x-badges-sale-eager-loaded')
        ) {
          return;
        }

        const resolvedPosition = this.getResolvedPosition(label, productData);

        if (productData.container === 'product-info') {
          el.innerHTML += this.processTemplate(el, label, productData);
          return;
        }

        if (resolvedPosition === 'custom') {
          let container = el.querySelector('.custom-label-container');
          if (!container) {
            container = document.createElement('div');
            container.setAttribute('class', 'custom-label-container pointer-events-none');
            el.appendChild(container);
          }
          container.innerHTML += this.processTemplate(el, label, productData);
          return;
        }

        let container = el.querySelector(`.${resolvedPosition}-container`);
        if (!container) {
          container = this.createFixedPositionContainer(resolvedPosition);
          el.appendChild(container);
        }

        container.innerHTML += this.processTemplate(el, label, productData);
      },

      createFixedPositionContainer(position) {
        let HTMLClass = `${position}-container label-container absolute gap-1 space-y-1`;
        HTMLClass += position.includes('top') ? ' top-2 flex-col' : ' bottom-2 flex-col-reverse';
        HTMLClass += position.includes('left') ? ' left-1' : ' right-1';

        const container = document.createElement('div');
        container.setAttribute('class', HTMLClass);

        return container;
      },

      // 할인 기준가 계산
      getSaleCompareBase(productData) {
        const price = Number(productData?.price || 0);
        const msrp = Number(productData?.msrp_price || 0);
        const compareAt = Number(productData?.compare_at_price || 0);

        if (msrp && msrp > price) return msrp;
        return compareAt;
      },

      // 할인율 문자열 계산
      getSalePercentString(productData) {
        const price = Number(productData?.price || 0);
        const baseCompare = Number(this.getSaleCompareBase(productData) || 0);

        if (!baseCompare || baseCompare <= price) return '';

        let sale = Math.round(((baseCompare - price) * 100) / baseCompare);
        sale = sale === 100 ? 99 : sale;
        return sale > 0 ? `${sale}%` : '';
      },

      // 할인 금액 문자열 계산
      getSaleAmountString(productData) {
        const raw = productData?.sale_amount_msrp || productData?.sale_amount || '';
        if (!raw) return '';
        if (raw.includes && raw.includes('-')) return '';
        return raw;
      },

      processContent(el, label, productData) {
        let content = false;
        const canShow = this.canShow(label, productData);
        const resolvedPosition = this.getResolvedPosition(label, productData);

        if (label.settings.image && canShow) {
          let imageHeight;
          let imageWidth;

          if (productData.container === 'product-info') {
            imageHeight = 126;
            imageWidth = Math.round(imageHeight * label.settings.image_aspect_ratio);
          } else {
            imageWidth =
              label.settings.size_mobile > label.settings.size
                ? label.settings.size_mobile * 15
                : label.settings.size * 15;
            imageHeight = imageWidth / label.settings.image_aspect_ratio;
          }

          let image;
          if (label.settings.image.src) {
            image = label.settings.image.src.includes('burst.shopifycdn.com')
              ? label.settings.image.src
              : label.settings.image.src + `&width=` + imageWidth * 3;
          } else {
            image = label.settings.image.includes('burst.shopifycdn.com')
              ? label.settings.image
              : label.settings.image + `&width=` + imageWidth * 3;
          }

          let imageDirection;
          let styleImage;

          if (productData.container === 'card') {
            imageDirection = resolvedPosition.includes('left') ? 'justify-start' : 'justify-end';
            styleImage = 'width: var(--width-image-label); height: var(--height-image-label)';
          } else {
            imageDirection = productData.make_content_center ? 'justify-center' : 'justify-start';
            styleImage = '';
          }

          content = `<div x-ref="content" class='x-badge-content flex ${imageDirection}{css-opacity}'>
            <img
              loading="lazy"
              width="${imageWidth}"
              height="${imageHeight}"
              alt="${label.settings.image_alt.length > 0 ? label.settings.image_alt : productData.title}"
              src="${image}"
              style="${styleImage}"
            />
          </div>`;
        } else if (label.settings.content && canShow) {
          const qty = productData.inventory_management.length < 1 || productData.qty < 0 ? '' : productData.qty;
          const saleAmount = this.getSaleAmountString(productData);
          const countDown = label.settings.schedule_enabled
            ? '<span x-intersect.once="$nextTick(() => { if (typeof rePosition !== `undefined`) { rePosition() } });" class="x-badge-countdown-' +
              label.id +
              ' label-countdown empty:hidden"></span>'
            : '';

          const sale = this.getSalePercentString(productData);

          content = label.settings.content
            .replace(/{sale}/gi, sale)
            .replace(/{sale_amount}/gi, saleAmount)
            .replace(/{qty}/gi, qty)
            .replace(/{price}/gi, productData.price_with_currency)
            .replace(/{count_down}/gi, countDown);

          if (productData.metafield_label) {
            Object.entries(productData.metafield_label).forEach(([key, value]) => {
              content = content.replace(`{${key}}`, value);
            });
          }

          const padding = label.settings.size / 2;
          const paddingMobile = label.settings.size_mobile / 2;
          const sizeClass =
            productData.container === 'product-info'
              ? ''
              : ` pt-${paddingMobile} pb-${paddingMobile} pl-${paddingMobile + 1.5} pr-${paddingMobile + 1.5} md:pt-${padding} md:pb-${padding} md:pl-${padding + 1.5} md:pr-${padding + 1.5}`;
          const inlineStyle =
            productData.container === 'product-info' ? '' : `style="font-size: var(--font-size-scale);"`;
          const inlineStyleIcon =
            productData.container === 'product-info'
              ? ''
              : `style="height: var(--font-size-scale); width: var(--font-size-scale); min-width: var(--font-size-scale);"`;

          content =
            content.length > 0
              ? `<div
                  x-ref="content"
                  class='x-badge-content ltr x-badge-text select-none inline-flex justify-center${sizeClass} items-center{css-opacity}{css-type} gap-2'
                  ${inlineStyle}
                ><span class="icon-label empty:hidden" ${inlineStyleIcon}>${label.settings.icon}</span><span class="leading-normal w-fit p-break-words">${content}</span></div>`
              : false;

          if (countDown.length > 0 && label.settings.schedule_enabled) {
            Alpine.store('xHelper').countdown(label.settings, function (canShowCountdown, seconds, minutes, hours, days) {
              const container = el.container ? el.container : el;
              const countdownElements = container.getElementsByClassName('x-badge-countdown-' + label.id);

              if (!canShowCountdown) {
                for (let i = 0; i < countdownElements.length; i++) {
                  countdownElements[i].innerHTML = '';
                }
                return;
              }

              days = days > 0 ? days + 'D&nbsp;&nbsp;&nbsp;' : '';
              hours = hours === 0 && days.length === 0 ? '' : hours + ' : ';
              const timeLeft = days + hours + minutes + ' : ' + seconds;

              for (let i = 0; i < countdownElements.length; i++) {
                countdownElements[i].innerHTML = timeLeft;
              }
            });
          }
        }

        return content;
      },

      processTemplate(el, label, productData) {
        let template = '';
        let teleportTemplateOpen = '';
        let teleportTemplateClose = '';

        const content = this.processContent(el, label, productData);
        if (!content) return template;

        const resolvedPosition = this.getResolvedPosition(label, productData);
        const resolvedCustomPosition = this.getResolvedCustomPosition(label, productData);
        const resolvedHorizontal = resolvedCustomPosition.horizontal;
        const resolvedVertical = resolvedCustomPosition.vertical;

        const cssOpacity = ' opacity-' + label.settings.opacity;
        let cssPosition = '';
        let containerCssClass = '';

        if (productData.container === 'product-info' && resolvedPosition === 'custom' && productData.position_adapt) {
          cssPosition =
            'left: ' +
            resolvedHorizontal +
            '%;' +
            ' transform: translate(-' +
            resolvedHorizontal +
            '%, -' +
            resolvedVertical +
            '%);' +
            ' top: ' +
            resolvedVertical +
            '%;';
        } else {
          cssPosition =
            productData.container === 'card'
              ? 'left: ' +
                resolvedHorizontal +
                '%;' +
                ' transform: translate(-' +
                resolvedHorizontal +
                '%, -' +
                resolvedVertical +
                '%);' +
                ' top: ' +
                resolvedVertical +
                '%;'
              : '';
        }

        let cssType = '';
        if (label.settings.type === 'round') cssType = ' rounded-md';
        if (label.settings.type === 'rounded-full') cssType = ' rounded-full';

        if (productData.container === 'card') {
          containerCssClass = ' absolute w-max';
        } else if (productData.position_adapt) {
          containerCssClass =
            resolvedPosition === 'custom'
              ? ' absolute w-max h-fit product-info-custom-label'
              : ' w-fit h-fit product-info-custom-label';
        }

        if (productData.container === 'product-info' && productData.position_adapt) {
          if (productData.teleport_dest_price || productData.teleport_dest_image) {
            teleportTemplateClose = '</template>';
          }

          if (resolvedPosition === 'next-price') {
            if (productData.teleport_dest_price) {
              teleportTemplateOpen = `<template x-teleport="${productData.teleport_dest_price}">`;
            }
          } else if (resolvedPosition === 'custom') {
            if (productData.teleport_dest_image) {
              teleportTemplateOpen = `<template x-teleport="${productData.teleport_dest_image}">`;
            }
          } else if (resolvedPosition === 'below-image' || resolvedPosition === 'bottom-card') {
            teleportTemplateOpen = '';
            teleportTemplateClose = '';
          } else {
            const imageContainer = productData.teleport_dest_image
              ? document.querySelector(productData.teleport_dest_image)
              : null;

            if (imageContainer) {
              let container = imageContainer.querySelector(`.${resolvedPosition}-container`);
              if (!container) {
                container = this.createFixedPositionContainer(resolvedPosition);
                imageContainer.appendChild(container);
              }

              teleportTemplateOpen = `<template x-teleport="${productData.teleport_dest_image} .${resolvedPosition}-container">`;
            } else {
              teleportTemplateOpen = '';
              teleportTemplateClose = '';
            }
          }
        }

        containerCssClass += resolvedHorizontal > 50 ? ' text-end' : ' text-start';

        const previewShowCondition = productData.isXBadgesPreview
          ? `x-show="$store.xBadges.previewActiveBlock == '{label-id}'"`
          : '';

        let imgClass;
        if (
          productData.container === 'product-info' &&
          (resolvedPosition === 'below-image' || resolvedPosition === 'bottom-card' || !productData.position_adapt)
        ) {
          const imageContainerAlignment = productData.make_content_center ? 'flex justify-center' : '';
          imgClass = label.settings.image ? ` label-img ${imageContainerAlignment}` : '';
        } else {
          imgClass = label.settings.image ? ' label-img' : '';
        }

        template = this.getLableTemplate(productData.container, resolvedPosition);
        template = template
          .replace('{preview-show-condition}', previewShowCondition)
          .replace('{content}', content)
          .replace('{css-opacity}', cssOpacity)
          .replace('{css-position}', cssPosition)
          .replace('{css-type}', cssType)
          .replace('{container-css-class}', containerCssClass)
          .replace('{container-img-class}', imgClass)
          .replace('{teleport-template-open}', teleportTemplateOpen)
          .replace('{teleport-template-close}', teleportTemplateClose)
          .replace(/{label-id}/gi, label.id);

        return template;
      },

      getLableTemplate(container, position) {
        if (container === 'product-info') {
          return this.productDetailTemplate;
        } else if (position === 'custom') {
          return this.customPositionTemplate;
        } else if (position === 'below-image' || position === 'bottom-card' || position === 'next-price') {
          return this.outSideImageTemplate.replace('{queryEL}', position);
        }

        return this.fixedPositionTemplate;
      },

      canShow(label, productData) {
        if (productData.isXBadgesPreview) {
          return true;
        }

        if (productData.container === 'card' && !label.settings.show_on_product_card) {
          return false;
        }

        if (productData.container === 'product-info' && !label.settings.show_on_product_page) {
          return false;
        }

        if (label.type === 'sale-label') {
          const price = Number(productData?.price || 0);
          const baseCompare = Number(this.getSaleCompareBase(productData) || 0);
          if (baseCompare > price) return true;
        }

        if (label.type === 'sold-out-label' && !productData.available) {
          return true;
        }

        if (label.type === 'preorder-label') {
          if (productData.can_show_preorder && productData.available) {
            return true;
          }
          return false;
        }

        if (label.type === 'new-label') {
          if (label.settings.day_since === 'creation_date' && label.settings.number_show < productData.diff_day_create) {
            return false;
          } else if (
            label.settings.day_since === 'activation_date' &&
            label.settings.number_show < productData.diff_day_publish
          ) {
            return false;
          }
        }

        if (label.settings.schedule_enabled) {
          const endDate = new Date(
            label.settings.end_year,
            label.settings.end_month - 1,
            label.settings.end_day,
            label.settings.end_hour,
            label.settings.end_minute
          );
          label.endTime =
            endDate.getTime() + (-1 * label.settings.timezone * 60 - endDate.getTimezoneOffset()) * 60 * 1000;

          const startDate = new Date(
            label.settings.start_year,
            label.settings.start_month - 1,
            label.settings.start_day,
            label.settings.start_hour,
            label.settings.start_minute
          );
          label.startTime =
            startDate.getTime() + (-1 * label.settings.timezone * 60 - startDate.getTimezoneOffset()) * 60 * 1000;

          const now = new Date().getTime();
          if (label.endTime < now) {
            return false;
          }

          if (label.startTime > now) {
            return false;
          }
        }

        if (label.settings.applied_products.includes(productData.id)) {
          return true;
        }

        for (let i = 0; i < label.settings.applied_collections.length; i++) {
          if (productData.collections.includes(label.settings.applied_collections[i])) {
            return true;
          }
        }

        if (
          label.type !== 'sale-label' &&
          label.type !== 'sold-out-label' &&
          label.settings.applied_products.length === 0 &&
          label.settings.applied_collections.length === 0
        ) {
          return true;
        }

        return false;
      }
    });
  });
});


/* assets/labels-and-badges.js
 * MSRP-first sale badge logic (Reclop)
 * - If productData.msrp_price exists and is greater than productData.price,
 *   use it as the compare base for {sale} and sale-label visibility.
 * - Otherwise, fallback to Shopify compare_at_price behavior.
 *
 * NOTE:
 * This file assumes productData may optionally include:
 *  - msrp_price: number (in cents)
 *  - sale_amount_msrp: string (formatted money)
 */

// requestAnimationFrame(() => {
//   document.addEventListener('alpine:init', () => {
//     Alpine.store('xBadges', {
//       fixedPositionTemplate: `<div
//         class="x-badge-{label-id} x-badge-container pointer-events-none{container-img-class} ltr"
//         {preview-show-condition}
//       >
//         {content}
//       </div>`,
//       customPositionTemplate: `<div
//         class="x-badge-{label-id} x-badge-container min-w-fit max-w-full max-h-full pointer-events-none{container-css-class} ltr"
//         x-data="{
//           contentHeight: 1,
//           rePosition() {
//             this.$nextTick(() => {
//               this.contentHeight = this.$refs.content ? this.$refs.content.offsetHeight : contentHeight;
//             });
//           }
//         }"
//         x-intersect.once="rePosition();
//           if (Shopify.designMode) {
//             window.addEventListener('resize', () => {
//               if ($store.xBadges.lastWindowWidth != window.innerWidth) {
//                 rePosition();
//               }
//             });
//           } else {
//             installMediaQueryWatcher('(min-width: 768px)', (matches) => rePosition());
//           }"
//         {preview-show-condition}
//         :style="'{css-position} min-height: ' + contentHeight + 'px'"
//       >
//         {content}
//       </div>`,
//       productDetailTemplate: `
//       {teleport-template-open}
//         <div
//           class="x-badge-{label-id} x-badge-container min-w-fit max-w-full max-h-full bottom-0 pointer-events-none{container-css-class}{container-img-class}"
//           style="{css-position}"
//           x-data="{
//             contentHeight: 1,
//             rePosition() {
//               this.$nextTick(() => {
//                 this.contentHeight = this.$refs.content ? this.$refs.content.offsetHeight : contentHeight;
//               });
//             }
//           }"
//           x-intersect.once="rePosition();
//             if (Shopify.designMode) {
//               window.addEventListener('resize', () => {
//                 if ($store.xBadges.lastWindowWidth != window.innerWidth) {
//                   rePosition();
//                 }
//               });
//             } else {
//               installMediaQueryWatcher('(min-width: 768px)', (matches) => rePosition());
//             }"
//         >
//           {content}
//         </div>
//       {teleport-template-close}
//       `,
//       outSideImageTemplate: `<div class="hidden"
//         x-data="{
//           setPosition() {
//             const element = $el.closest('.card-product').getElementsByClassName('{queryEL}');
//             for (let i = 0; i < element.length; i++) {
//               element[i].innerHTML += $el.innerHTML;
//             }
//           }
//         }"
//         x-init="setPosition()">
//         <div
//           class="x-badge-{label-id} x-badge-container min-w-fit max-w-full max-h-full bottom-0 pointer-events-none{container-img-class}"
//           {preview-show-condition}
//         >
//           {content}
//         </div>
//       </div>`,

//       previewActiveBlock: window.xBadgesPreviewActiveBlock,

//       init() {
//         if (Shopify.designMode) {
//           document.addEventListener('shopify:block:select', (event) => {
//             if (!event.target.classList.contains('x-badges-block-preview')) return;

//             let blockId = event.target.getAttribute('block-id');
//             this.previewActiveBlock = blockId;
//             window.xBadgesPreviewActiveBlock = blockId;
//             document.dispatchEvent(new CustomEvent('eurus:badges:block-select'));
//           });
//         }
//       },

//       load(el, callback = () => {}, container = null) {
//         if (container) el.container = container;

//         const sliderEl = el.closest('[x-data-slider]');
//         if (sliderEl) {
//           if (!sliderEl.classList.contains('is-initialized')) {
//             const sectionId = el.closest('[x-data-slider]').getAttribute('x-data-slider');
//             document.addEventListener(`eurus:${sectionId}:splide-ready`, () => {
//               this.doLoad(el, callback);
//             });
//           } else {
//             this.doLoad(el, callback);
//           }
//         } else {
//           this.doLoad(el, callback);
//         }
//       },

//       doLoad(el, callback = () => {}) {
//         this.initAllLabels(el);

//         if (Shopify.designMode) {
//           let productData = xParseJSON(el.getAttribute('x-labels-data'));
//           document.addEventListener('shopify:section:load', () => {
//             if (productData && !productData.isXBadgesPreview) {
//               this.initAllLabels(el);
//             }
//           });
//         }

//         callback(el);
//       },

//       initAllLabels(el) {
//         let productData = xParseJSON(el.getAttribute('x-labels-data'));
//         if (!productData) return;

//         if (Shopify.designMode) {
//           let currentLabels = el.getElementsByClassName('x-badge-container');
//           if (currentLabels.length > 0) {
//             const labelsNum = currentLabels.length;
//             for (let i = 0; i < labelsNum; i++) {
//               currentLabels[0].remove();
//             }
//           }
//         }

//         let allLabels = document.getElementsByClassName('x-badges-block-data');
//         for (let i = 0; i < allLabels.length; i++) {
//           let label = xParseJSON(allLabels[i].getAttribute('x-badges-block-data'));
//           if (!label.enable && !productData.isXBadgesPreview) continue;

//           label.settings.icon = allLabels[i].getAttribute('x-badges-icon');
//           this.appendLabel(el, label, productData);
//         }

//         el.removeAttribute('x-labels-data');
//       },

//       appendLabel(el, label, productData) {
//         if (productData.container == 'product-info') {
//           el.innerHTML += this.processTemplate(el, label, productData);
//           return;
//         }

//         if (label.settings.position == 'custom') {
//           let container = el.querySelector(`.custom-label-container`);
//           if (!container) {
//             container = document.createElement("div");
//             let HTMLClass = `custom-label-container pointer-events-none`;
//             container.setAttribute('class', HTMLClass);
//             el.appendChild(container);
//           }
//           container.innerHTML += this.processTemplate(el, label, productData);
//           return;
//         }

//         let container = el.querySelector(`.${label.settings.position}-container`);
//         if (!container) {
//           container = this.createFixedPositionContainer(label.settings.position);
//           el.appendChild(container);
//         }

//         container.innerHTML += this.processTemplate(el, label, productData);
//       },

//       createFixedPositionContainer(position) {
//         let HTMLClass = `${position}-container label-container absolute gap-1 space-y-1`;
//         HTMLClass += position.includes('top') ? ' top-1 flex-col' : ' bottom-1 flex-col-reverse';
//         HTMLClass += position.includes('left') ? ' left-1' : ' right-1';

//         container = document.createElement('div');
//         container.setAttribute('class', HTMLClass);

//         return container;
//       },

//       /**
//        * Decide which price to use as the "compare" base for sale calculations.
//        * MSRP-first:
//        *  - If msrp_price exists and is greater than price, use it.
//        *  - Else fallback to compare_at_price.
//        */
//       getSaleCompareBase(productData) {
//         const price = Number(productData?.price || 0);
//         const msrp = Number(productData?.msrp_price || 0);
//         const compareAt = Number(productData?.compare_at_price || 0);

//         if (msrp && msrp > price) return msrp;
//         return compareAt;
//       },

//       /**
//        * Calculate sale percent string (e.g., "50%") using base compare and price.
//        */
//       getSalePercentString(productData) {
//         const price = Number(productData?.price || 0);
//         const baseCompare = Number(this.getSaleCompareBase(productData) || 0);

//         if (!baseCompare || baseCompare <= price) return '';

//         let sale = Math.round(((baseCompare - price) * 100) / baseCompare);
//         sale = sale === 100 ? 99 : sale;
//         return sale > 0 ? `${sale}%` : '';
//       },

//       /**
//        * Get sale amount display string.
//        * MSRP-first:
//        *  - Use sale_amount_msrp if present
//        *  - Else fallback to sale_amount
//        */
//       getSaleAmountString(productData) {
//         const raw = productData?.sale_amount_msrp || productData?.sale_amount || '';
//         if (!raw) return '';
//         if (raw.includes && raw.includes('-')) return '';
//         return raw;
//       },

//       processContent(el, label, productData) {
//         let content = false;
//         const canShow = this.canShow(label, productData);
//         if (label.settings.image && canShow) {
//           /** image label */
//           let imageHeight, imageWidth;

//           if (productData.container == 'product-info') {
//             imageHeight = 126;
//             imageWidth = Math.round(imageHeight * label.settings.image_aspect_ratio);
//           } else {
//             imageWidth =
//               label.settings.size_mobile > label.settings.size
//                 ? label.settings.size_mobile * 15
//                 : label.settings.size * 15;
//             imageHeight = imageWidth / label.settings.image_aspect_ratio;
//           }

//           let image;
//           if (label.settings.image.src) {
//             image = label.settings.image.src.includes('burst.shopifycdn.com')
//               ? label.settings.image.src
//               : label.settings.image.src + `&width=` + imageWidth * 3;
//           } else {
//             image = label.settings.image.includes('burst.shopifycdn.com')
//               ? label.settings.image
//               : label.settings.image + `&width=` + imageWidth * 3;
//           }

//           if (productData.container == 'card') {
//             var imageDirection = label.settings.position.includes('left') ? 'justify-start' : 'justify-end';
//             var styleImage = 'width: var(--width-image-label); height: var(--height-image-label)';
//           } else {
//             var imageDirection = productData.make_content_center ? 'justify-center' : 'justify-start';
//             var styleImage = '';
//           }

//           content = `<div x-ref="content" class='x-badge-content flex ${imageDirection}{css-opacity}'>
//             <img
//               loading="lazy"
//               width="` + imageWidth + `"
//               height="` + imageHeight + `"
//               alt="` + (label.settings.image_alt.length > 0 ? label.settings.image_alt : productData.title) + `"
//               src="` + image + `"
//               style="${styleImage}"
//             />
//           </div>`;
//         } else if (label.settings.content && canShow) {
//           /** text label */
//           let qty = productData.inventory_management.length < 1 || productData.qty < 0 ? '' : productData.qty;
//           let saleAmount = this.getSaleAmountString(productData);
//           let countDown = label.settings.schedule_enabled
//             ? '<span x-intersect.once="$nextTick(() => { if (typeof rePosition !== `undefined`) {rePosition()} });" class="x-badge-countdown-' +
//               label.id +
//               ' label-countdown empty:hidden"></span>'
//             : '';

//           let sale = this.getSalePercentString(productData);
//           content = label.settings.content
//             .replace(/{sale}/gi, sale)
//             .replace(/{sale_amount}/gi, saleAmount)
//             .replace(/{qty}/gi, qty)
//             .replace(/{price}/gi, productData.price_with_currency)
//             .replace(/{count_down}/gi, countDown);

//           if (productData.metafield_label) {
//             Object.entries(productData.metafield_label).forEach(([key, value]) => {
//               content = content.replace(`{${key}}`, value);
//             });
//           }

//           const padding = label.settings.size / 2;
//           const padding_mobile = label.settings.size_mobile / 2;
//           const sizeClass =
//             productData.container == 'product-info'
//               ? ''
//               : ` pt-${padding_mobile} pb-${padding_mobile} pl-${padding_mobile + 1.5} pr-${padding_mobile + 1.5} md:pt-${padding} md:pb-${padding} md:pl-${padding + 1.5} md:pr-${padding + 1.5}`;
//           const inlineStyle = productData.container == 'product-info' ? '' : `style="font-size: var(--font-size-scale);"`;
//           const inlineStyleIcon =
//             productData.container == 'product-info'
//               ? ''
//               : `style="height: var(--font-size-scale); width: var(--font-size-scale); min-width: var(--font-size-scale);"`;

//           content =
//             content.length > 0
//               ? `<div
//                   x-ref="content"
//                   class='x-badge-content ltr x-badge-text select-none inline-flex justify-center${sizeClass} items-center{css-opacity}{css-type} gap-2'
//                   ${inlineStyle}
//                 ><span class="icon-label empty:hidden" ${inlineStyleIcon}>${label.settings.icon}</span><span class="leading-normal w-fit p-break-words">${content}</span></div>`
//               : false;

//           if (countDown.length > 0 && label.settings.schedule_enabled) {
//             Alpine.store('xHelper').countdown(label.settings, function (canShow, seconds, minutes, hours, days) {
//               let container = el.container ? el.container : el;
//               const countdownElements = container.getElementsByClassName('x-badge-countdown-' + label.id);

//               if (!canShow) {
//                 for (let i = 0; i < countdownElements.length; i++) {
//                   countdownElements[i].innerHTML = '';
//                 }
//                 return;
//               }

//               days = days > 0 ? days + 'D&nbsp;&nbsp;&nbsp;' : '';
//               hours = hours == 0 && days.length == 0 ? '' : hours + ' : ';
//               const timeLeft = days + hours + minutes + ' : ' + seconds;

//               for (let i = 0; i < countdownElements.length; i++) {
//                 countdownElements[i].innerHTML = timeLeft;
//               }
//             });
//           }
//         }

//         return content;
//       },

//       processTemplate(el, label, productData) {
//         let template = '';
//         let teleportTemplateOpen = '';
//         let teleportTemplateClose = '';
//         if (content = this.processContent(el, label, productData)) {
//           const cssOpacity = " opacity-" + label.settings.opacity;
//           let cssPosition = ''
//           let containerCssClass = ''

//           if (productData.container == "product-info" && label.settings.position == "custom" && productData.position_adapt) {
//             cssPosition = "left: " + label.settings.horizontal_position + "%;" + " transform: translate(-" + label.settings.horizontal_position+"%, -" + label.settings.vertical_position+"%);" + " top: " + (label.settings.vertical_position) + "%;";
//           } else {
//             cssPosition = productData.container == "card" ? "left: " + label.settings.horizontal_position + "%;" + " transform: translate(-"+ label.settings.horizontal_position+"%, -"+ label.settings.vertical_position+"%);"
//             + " top: " + (label.settings.vertical_position) + "%;"
//             : "";
//           }
//           let cssType = '';
//           if (label.settings.type == 'round') cssType = ' rounded-md';
//           if (label.settings.type == 'rounded-full') cssType = ' rounded-full';

//           if (productData.container === "card") {
//             containerCssClass = " absolute w-max";
//           } else if (productData.position_adapt) {
//             containerCssClass = (label.settings.position === "custom") ? " absolute w-max h-fit product-info-custom-label" : " w-fit h-fit product-info-custom-label";
//           }

//           if (productData.container == "product-info" && productData.position_adapt) {
//             if (productData.teleport_dest_price || productData.teleport_dest_image) {
//               teleportTemplateClose = "</template>"
//             }
//             if (label.settings.position == "next-price") {
//               if (productData.teleport_dest_price) {
//                 teleportTemplateOpen = `<template x-teleport="${productData.teleport_dest_price}">`;
//               }
//             } else if (label.settings.position == "custom") {
//               // console.log('aaaaaaaaaaaaaa');
//               if (productData.teleport_dest_image) {
//                 teleportTemplateOpen = `<template x-teleport="${productData.teleport_dest_image}">`
//               }
//             } else if (label.settings.position == "below-image" || label.settings.position == "bottom-card") {
//               teleportTemplateOpen = "";
//               teleportTemplateClose = "";
//             } else {
//               const imageContainer = document.querySelector(productData.teleport_dest_image);
//               let container = imageContainer.querySelector(`.${label.settings.position}-container`);
//               // console.log(container);
//               if (!container) {
//                 container = this.createFixedPositionContainer(label.settings.position);
//                 imageContainer.appendChild(container);
//               }
//               if (productData.teleport_dest_image) {
//                 teleportTemplateOpen = `<template x-teleport="${productData.teleport_dest_image} .${label.settings.position}-container">`
//               }
//             }
//           }

//           containerCssClass += label.settings.horizontal_position > 50 ? " text-end" : " text-start";
//           const previewShowCondition = productData.isXBadgesPreview ? `x-show="$store.xBadges.previewActiveBlock == '{label-id}'"` : '';
//           let imgClass;
//           if (productData.container == "product-info" && (label.settings.position == "below-image" || label.settings.position == "bottom-card" || !productData.position_adapt)) {
//             let imageContainerAlignment = productData.make_content_center ? 'flex justify-center' : ''
//             imgClass = label.settings.image ? ` label-img ${imageContainerAlignment}` : '';
//           } else {
//             imgClass = label.settings.image ? ' label-img' : '';
//           }
//           template = this.getLableTemplate(productData.container, label.settings.position);
//           // console.log(teleportTemplateOpen);
//           // console.log(template);
//           template = template.replace('{preview-show-condition}', previewShowCondition)
//             .replace('{content}', content)
//             .replace('{css-opacity}', cssOpacity)
//             .replace('{css-position}', cssPosition)
//             .replace('{css-type}', cssType)
//             .replace('{container-css-class}', containerCssClass)
//             .replace('{container-img-class}', imgClass)
//             .replace('{teleport-template-open}', teleportTemplateOpen)
//             .replace('{teleport-template-close}', teleportTemplateClose)
//             .replace(/{label-id}/gi, label.id);
//         }
//         // console.log(template);
//         return template;
//       },
      
//       getLableTemplate(container, position) {
//         if (container == 'product-info') {
//           return this.productDetailTemplate;
//         } else if (position == 'custom') {
//           return this.customPositionTemplate;
//         } else if (position == 'below-image' || position == 'bottom-card' || position == 'next-price') {
//           return this.outSideImageTemplate.replace('{queryEL}', position);
//         }

//         return this.fixedPositionTemplate;
//       },

//       canShow(label, productData) {
//         if (productData.isXBadgesPreview) {
//           return true;
//         }

//         if (productData.container == 'card' && !label.settings.show_on_product_card) {
//           return false;
//         }
//         if (productData.container == 'product-info' && !label.settings.show_on_product_page) {
//           return false;
//         }

//         if (label.type == 'sale-label') {
//           const price = Number(productData?.price || 0);
//           const baseCompare = Number(this.getSaleCompareBase(productData) || 0);
//           if (baseCompare > price) return true;
//         }

//         if (label.type == 'sold-out-label' && !productData.available) {
//           return true;
//         }

//         if (label.type == 'preorder-label') {
//           if (productData.can_show_preorder && productData.available) {
//             return true;
//           } else {
//             return false;
//           }
//         }

//         if (label.type == 'new-label') {
//           if (label.settings.day_since == 'creation_date' && label.settings.number_show < productData.diff_day_create) {
//             return false;
//           } else if (
//             label.settings.day_since == 'activation_date' &&
//             label.settings.number_show < productData.diff_day_publish
//           ) {
//             return false;
//           }
//         }

//         if (label.settings.schedule_enabled) {
//           let endDate = new Date(
//             label.settings.end_year,
//             label.settings.end_month - 1,
//             label.settings.end_day,
//             label.settings.end_hour,
//             label.settings.end_minute
//           );
//           label.endTime =
//             endDate.getTime() + (-1 * label.settings.timezone * 60 - endDate.getTimezoneOffset()) * 60 * 1000;

//           let startDate = new Date(
//             label.settings.start_year,
//             label.settings.start_month - 1,
//             label.settings.start_day,
//             label.settings.start_hour,
//             label.settings.start_minute
//           );
//           label.startTime =
//             startDate.getTime() + (-1 * label.settings.timezone * 60 - startDate.getTimezoneOffset()) * 60 * 1000;

//           let now = new Date().getTime();
//           if (label.endTime < now) {
//             return false;
//           }

//           if (label.startTime > now) {
//             return false;
//           }
//         }

//         if (label.settings.applied_products.includes(productData.id)) {
//           return true;
//         }

//         for (let i = 0; i < label.settings.applied_collections.length; i++) {
//           if (productData.collections.includes(label.settings.applied_collections[i])) {
//             return true;
//           }
//         }

//         if (
//           label.type != 'sale-label' &&
//           label.type != 'sold-out-label' &&
//           label.settings.applied_products.length == 0 &&
//           label.settings.applied_collections.length == 0
//         ) {
//           return true;
//         }
//         return false;
//       }
//     });
//   });
// });



// 기존 로직 내용은 주석 처리

// requestAnimationFrame(() => {
//   document.addEventListener('alpine:init', () => {
//     Alpine.store('xBadges', {
//       fixedPositionTemplate: `<div
//         class="x-badge-{label-id} x-badge-container pointer-events-none{container-img-class} ltr"
//         {preview-show-condition}
//       >
//         {content}
//       </div>`,
//       customPositionTemplate: `<div
//         class="x-badge-{label-id} x-badge-container min-w-fit max-w-full max-h-full pointer-events-none{container-css-class} ltr"
//         x-data="{
//           contentHeight: 1,
//           rePosition() {
//             this.$nextTick(() => {
//               this.contentHeight = this.$refs.content ? this.$refs.content.offsetHeight : contentHeight;
//             });
//           }
//         }"
//         x-intersect.once="rePosition();
//           if (Shopify.designMode) {
//             window.addEventListener('resize', () => {
//               if ($store.xBadges.lastWindowWidth != window.innerWidth) {
//                 rePosition();
//               }
//             });
//           } else {
//             installMediaQueryWatcher('(min-width: 768px)', (matches) => rePosition());
//           }"
//         {preview-show-condition}
//         :style="'{css-position} min-height: ' + contentHeight + 'px'"
//       >
//         {content}
//       </div>`,
//       productDetailTemplate: `<div
//         class="x-badge-{label-id} x-badge-container min-w-fit max-w-full max-h-full bottom-0 pointer-events-none{container-css-class}{container-img-class}"
//       >
//         {content}
//       </div>`,
//       outSideImageTemplate: `<div class="hidden" 
//         x-data="{
//           setPosition() {
//             const element = $el.closest('.card-product').getElementsByClassName('{queryEL}');
//             for (let i = 0; i < element.length; i++) {
//               element[i].innerHTML += $el.innerHTML;
//             }
//           }
//         }"
//         x-init="setPosition()">
//         <div
//           class="x-badge-{label-id} x-badge-container min-w-fit max-w-full max-h-full bottom-0 pointer-events-none{container-img-class}"
//           {preview-show-condition}
//         >
//           {content}
//         </div>
//       </div>`,
//       previewActiveBlock: window.xBadgesPreviewActiveBlock,
//       init() {
//         if (Shopify.designMode) {
//           document.addEventListener('shopify:block:select', (event) => {
//             if (!event.target.classList.contains('x-badges-block-preview')) return;

//             let blockId = event.target.getAttribute('block-id');
//             this.previewActiveBlock = blockId;
//             window.xBadgesPreviewActiveBlock = blockId;
//             document.dispatchEvent(new CustomEvent("eurus:badges:block-select"));
//           });
//         }
//       },
//       load(el, callback = () => {}, container = null) {
//         if (container) el.container = container;

//         const sliderEl = el.closest('[x-data-slider]');
//         if (sliderEl) {
//           if (!sliderEl.classList.contains('is-initialized')) {
//             const sectionId = el.closest('[x-data-slider]').getAttribute('x-data-slider');
//             document.addEventListener(`eurus:${sectionId}:splide-ready`, () => {
//               this.doLoad(el, callback);
//             });
//           } else {
//             this.doLoad(el, callback);
//           }
//         } else {
//           this.doLoad(el, callback);
//         }
//       },
//       doLoad(el, callback = () => {}) {
//         this.initAllLabels(el);

//         if (Shopify.designMode) {
//           let productData = xParseJSON(el.getAttribute('x-labels-data'));
//           document.addEventListener('shopify:section:load', () => {
//             if (productData && !productData.isXBadgesPreview) {
//               this.initAllLabels(el);
//             }
//           });
//         }

//         callback(el);
//       },
//       initAllLabels(el) {
//         let productData = xParseJSON(el.getAttribute('x-labels-data'));

//         if (!productData) return;

//         if (Shopify.designMode) {
//           let currentLabels = el.getElementsByClassName('x-badge-container');
//           if (currentLabels.length > 0) {
//             const labelsNum = currentLabels.length;
//             for (let i=0;i<labelsNum;i++) {
//               currentLabels[0].remove();
//             }
//           }
//         }

//         let allLabels = document.getElementsByClassName('x-badges-block-data');
//         for (let i = 0;i < allLabels.length;i++) {
//           let label = xParseJSON(allLabels[i].getAttribute('x-badges-block-data'));
//           if (!label.enable && !productData.isXBadgesPreview) return;
          
//           label.settings.icon = allLabels[i].getAttribute('x-badges-icon');
//           this.appendLabel(el, label, productData);
//         }

//         el.removeAttribute('x-labels-data');
//       },
//       appendLabel(el, label, productData) {
//         if (productData.container == 'product-info' || label.settings.position == 'custom') {
//           el.innerHTML += this.processTemplate(el, label, productData);
//           return;
//         }

//         let container = el.querySelector(`.${label.settings.position}-container`);
//         if (!container) {
//           container = this.createFixedPositionContainer(label.settings.position);
//           el.appendChild(container);
//         }

//         container.innerHTML += this.processTemplate(el, label, productData);
//       },
//       createFixedPositionContainer(position) {
//         let HTMLClass = `${position}-container label-container absolute gap-1 space-y-1`;
//         HTMLClass += position.includes('top') ? ' top-1 flex-col' : ' bottom-1 flex-col-reverse';
//         HTMLClass += position.includes('left') ? ' left-1' : ' right-1';

//         container = document.createElement("div");
//         container.setAttribute('class', HTMLClass);

//         return container;
//       },
//       processContent(el, label, productData) {
//         let content = false;
//         const canShow = this.canShow(label, productData);

//         if (label.settings.image && canShow) {
//           /** image label */
//           let imageHeight, imageWidth;
//           if (productData.container == 'product-info') {
//             imageHeight = 126;
//             imageWidth = Math.round(imageHeight * label.settings.image_aspect_ratio);
//           } else {
//             imageWidth = label.settings.size_mobile > label.settings.size ? label.settings.size_mobile * 15 : label.settings.size * 15;
//             imageHeight = imageWidth / label.settings.image_aspect_ratio;
//           }
//           let image;
//           if (label.settings.image.src) {
//             image = label.settings.image.src.includes('burst.shopifycdn.com') ? label.settings.image.src : 
//             label.settings.image.src + `&width=` + (imageWidth * 3);
//           } else {
//             image = label.settings.image.includes('burst.shopifycdn.com') ? label.settings.image : 
//             label.settings.image + `&width=` + (imageWidth * 3);
//           }
//           if (productData.container == "card") {
//             var imageDirection = label.settings.position.includes('left') ? "justify-start" : "justify-end";
//             var styleImage = 'width: var(--width-image-label); height: var(--height-image-label)';
//           } else {
//             var imageDirection = productData.make_content_center ? "justify-center" : "justify-start";
//             var styleImage = '';
//           }
//           content = `<div x-ref="content" class='x-badge-content flex ${imageDirection}{css-opacity}'>
//             <img 
//               loading="lazy"
//               width="` + imageWidth + `"
//               height="` + imageHeight + `"
//               alt="` + (label.settings.image_alt.length > 0 ? label.settings.image_alt : productData.title) + `"
//               src="` + image + `"
//               style="${styleImage}"
//             />
//           </div>`;
//         } else if (label.settings.content && canShow) {
//           /** text label */
//           let qty = (productData.inventory_management.length < 1 || productData.qty < 0) ? '' : productData.qty
//           let saleAmount = productData.sale_amount.includes('-') ? '' : productData.sale_amount;
//           let countDown = label.settings.schedule_enabled ? '<span x-intersect.once="$nextTick(() => { if (typeof rePosition !== `undefined`) {rePosition()} });" class="x-badge-countdown-' + label.id + ' label-countdown empty:hidden"></span>' : '';
//           let sale = Math.round((productData.compare_at_price - productData.price) * 100 / productData.compare_at_price);
//           sale = sale == 100 ? 99 : sale;
//           sale = sale > 0 ? sale + '%' : '';

//           content = label.settings.content.replace(/{sale}/gi, sale)
//                       .replace(/{sale_amount}/gi, saleAmount)
//                       .replace(/{qty}/gi, qty)
//                       .replace(/{price}/gi, productData.price_with_currency)
//                       .replace(/{count_down}/gi, countDown);

//           if (productData.metafield_label) {
//             Object.entries(productData.metafield_label).forEach(([key, value]) => {
//               content = content.replace(`{${key}}`, value);
//             });
//           }
//           const padding = label.settings.size / 2;
//           const padding_mobile = label.settings.size_mobile / 2;
//           const sizeClass = productData.container == 'product-info' ? '' : ` pt-${padding_mobile} pb-${padding_mobile} pl-${padding_mobile + 1.5} pr-${padding_mobile + 1.5} md:pt-${padding} md:pb-${padding} md:pl-${padding + 1.5} md:pr-${padding + 1.5}`;
//           const inlineStyle = productData.container == 'product-info' ? '' : `style="font-size: var(--font-size-scale);"`;
//           const inlineStyleIcon = productData.container == 'product-info' ? '' : `style="height: var(--font-size-scale); width: var(--font-size-scale); min-width: var(--font-size-scale);"`;
//           content = content.length > 0
//             ? `<div
//                 x-ref="content"
//                 class='x-badge-content ltr x-badge-text select-none inline-flex justify-center${sizeClass} items-center{css-opacity}{css-type} gap-2'
//                 ${inlineStyle}
//               ><span class="icon-label empty:hidden" ${inlineStyleIcon}>${label.settings.icon}</span><span class="leading-normal w-fit p-break-words">${content}</span></div>` : false;

//           if (countDown.length > 0 && label.settings.schedule_enabled) {
//             Alpine.store('xHelper').countdown(label.settings, function(canShow, seconds, minutes, hours, days) {
//               let container = el.container ? el.container : el;
//               const countdownElements = container.getElementsByClassName('x-badge-countdown-' + label.id);

//               if (!canShow) {
//                 for (let i = 0;i < countdownElements.length;i++) {
//                   countdownElements[i].innerHTML = '';
//                 }

//                 return;
//               }

//               days = days > 0 ? days + "D&nbsp;&nbsp;&nbsp;" : "";
//               hours = hours == 0 && days.length == 0 ? "" : hours + " : ";
//               const timeLeft = days + hours + minutes + " : " + seconds;

//               for (let i = 0;i < countdownElements.length;i++) {
//                 countdownElements[i].innerHTML = timeLeft;
//               }
//             });
//           }
//         }

//         return content;
//       },
//       processTemplate(el, label, productData) {
//         let template = '';
//         if (content = this.processContent(el, label, productData)) {
//           const cssOpacity = " opacity-" + label.settings.opacity;
//           const cssPosition = productData.container == "card" ? "left: " + label.settings.horizontal_position + "%;" + " transform: translate(-"+ label.settings.horizontal_position+"%, -"+ label.settings.vertical_position+"%);"
//                             + " top: " + (label.settings.vertical_position) + "%;"
//                             : "";
//           let cssType = '';
//           if (label.settings.type == 'round') cssType = ' rounded-md';
//           if (label.settings.type == 'rounded-full') cssType = ' rounded-full';

//           let containerCssClass = productData.container == "card" ? " absolute w-max" : "";
//           containerCssClass += label.settings.horizontal_position > 50 ? " text-end" : " text-start";
//           const previewShowCondition = productData.isXBadgesPreview ? `x-show="$store.xBadges.previewActiveBlock == '{label-id}'"` : '';
//           const imgClass = label.settings.image ? ' label-img' : '';

//           template = this.getLableTemplate(productData.container, label.settings.position);
//           template = template.replace('{preview-show-condition}', previewShowCondition)
//             .replace('{content}', content)
//             .replace('{css-opacity}', cssOpacity)
//             .replace('{css-position}', cssPosition)
//             .replace('{css-type}', cssType)
//             .replace('{container-css-class}', containerCssClass)
//             .replace('{container-img-class}', imgClass)
//             .replace(/{label-id}/gi, label.id);
//         }

//         return template;
//       },
//       getLableTemplate(container, position) {
//         if (container == 'product-info') {
//           return this.productDetailTemplate;
//         } else if (position == 'custom') {
//           return this.customPositionTemplate;
//         } else if (position == 'below-image' || position == 'bottom-card' || position == 'next-price') {
//           return this.outSideImageTemplate.replace('{queryEL}', position);
//         }

//         return this.fixedPositionTemplate;
//       },
//       canShow(label, productData) {
//         if (productData.isXBadgesPreview) {
//           return true;
//         }

//         if (productData.container == 'card' && !label.settings.show_on_product_card) {
//           return false;
//         }

//         if (productData.container == 'product-info' && !label.settings.show_on_product_page) {
//           return false;
//         }

//         if (label.type == "sale-label" && productData.compare_at_price > productData.price) {
//           return true;
//         }

//         if (label.type == "sold-out-label" && !productData.available) {
//           return true;
//         }
//         if (label.type == "preorder-label" ) {
//           if (productData.can_show_preorder && productData.available) {
//             return true;
//           } else {
//             return false;
//           }
//         }
//         if (label.type == "new-label" ) {
//           if (label.settings.day_since == 'creation_date' && label.settings.number_show < productData.diff_day_create) {
//             return false;
//           } else if (label.settings.day_since == 'activation_date' && label.settings.number_show < productData.diff_day_publish) {
//             return false;
//           }
//         }

//         if (label.settings.schedule_enabled) {
//           let endDate = new Date(
//             label.settings.end_year,
//             label.settings.end_month - 1,
//             label.settings.end_day,
//             label.settings.end_hour,
//             label.settings.end_minute
//           );
//           label.endTime = endDate.getTime()
//             + (-1 * label.settings.timezone * 60 - endDate.getTimezoneOffset()) * 60 * 1000;
  
//           let startDate = new Date(
//             label.settings.start_year,
//             label.settings.start_month - 1,
//             label.settings.start_day,
//             label.settings.start_hour,
//             label.settings.start_minute
//           );
//           label.startTime = startDate.getTime()
//             + (-1 * label.settings.timezone * 60 - startDate.getTimezoneOffset()) * 60 * 1000;

//           let now = new Date().getTime();
//           if (label.endTime < now) {
//             return false;
//           }

//           if (label.startTime > now) {
//             return false;
//           }
//         }

//         if (label.settings.applied_products.includes(productData.id)) {
//           return true;
//         }

//         for(let i=0;i<label.settings.applied_collections.length;i++) {
//           if (productData.collections.includes(label.settings.applied_collections[i])) {
//             return true;
//           }
//         }

//         if (label.type != "sale-label"
//           && label.type != "sold-out-label"
//           && label.settings.applied_products.length == 0
//           && label.settings.applied_collections.length == 0) {
//           return true;
//         }

//         return false;
//       }
//     });
//   });
// });
